import { Helmet } from "@dr.pogodin/react-helmet";

export default function Terms() {
  return (
    <article className="max-w-4xl mx-auto px-6 py-12">
      <Helmet>
        <title>Terms of Service | Free1099Calc</title>
        <meta
          name="description"
          content="Terms of Service for Free1099Calc - Read our terms and conditions for using the site including disclaimers and limitations of liability."
        />
        <link rel="canonical" href="https://free1099calc.com/terms" />
      </Helmet>

      <h1 className="text-3xl font-bold tracking-tight mb-2">Terms of Service</h1>
      <p className="text-sm text-slate-600 mb-8">Last updated: November 17, 2025</p>

      <p className="text-slate-700 mb-6">
        Welcome to Free1099Calc. These Terms of Service ("Terms") govern your use of the Free1099Calc website at <strong>free1099calc.com</strong> (the "Site") and the tax calculator tools and resources provided (the "Services"). By accessing or using our Site, you agree to be bound by these Terms.
      </p>

      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <p className="text-sm text-slate-800">
          <strong>⚠️ Important Disclaimer:</strong> Free1099Calc provides educational tools and information only. Our calculators and content are NOT professional tax, legal, or financial advice. Always consult a qualified CPA, tax professional, or financial advisor before making tax-related decisions.
        </p>
      </div>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">1. Acceptance of Terms</h2>
      <p className="text-slate-700 mb-4">
        By accessing, browsing, or using this Site, you acknowledge that you have read, understood, and agree to be bound by these Terms and our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>. If you do not agree with these Terms, you must not use the Site.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">2. Description of Services</h2>
      <p className="text-slate-700 mb-3">Free1099Calc provides:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Online tax calculators for 1099 contractors, freelancers, and self-employed individuals</li>
        <li>Retirement and inflation calculators</li>
        <li>Educational blog posts and articles about taxes, deductions, and financial planning</li>
        <li>Job market trends and insights</li>
      </ul>
      <p className="text-slate-700 mb-4">All calculations are estimates based on general tax rules and user inputs. Results may not reflect your actual tax liability.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">3. Educational Purpose Only - Not Tax Advice</h2>
      <p className="text-slate-700 mb-3"><strong>IMPORTANT:</strong> The information, calculators, and content provided on this Site are for educational and informational purposes only. They do NOT constitute:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Professional tax advice or preparation services</li>
        <li>Legal advice or legal services</li>
        <li>Financial planning or investment advice</li>
        <li>Accounting services</li>
        <li>A substitute for consultation with qualified professionals</li>
      </ul>

      <p className="text-slate-700 mb-4">
        Tax laws are complex and change frequently. Your individual circumstances (income sources, deductions, credits, state laws, etc.) may differ significantly from the assumptions used in our calculators. <strong>You are responsible for verifying all results and complying with applicable tax laws.</strong>
      </p>

      <p className="text-slate-700 mb-3">We strongly recommend consulting with a licensed CPA, Enrolled Agent, tax attorney, or other qualified tax professional before:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Filing your tax returns</li>
        <li>Making estimated tax payments</li>
        <li>Claiming deductions or credits</li>
        <li>Making business structure decisions (sole proprietor vs. S-Corp, etc.)</li>
        <li>Taking any action based on information from this Site</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">4. No Warranty - "As Is" Service</h2>
      <p className="text-slate-700 mb-3">The Site and Services are provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis without any warranties of any kind, either express or implied, including but not limited to:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Warranties of merchantability or fitness for a particular purpose</li>
        <li>Warranties that the Site will be uninterrupted, error-free, or secure</li>
        <li>Warranties regarding the accuracy, completeness, or reliability of any content, calculations, or results</li>
        <li>Warranties that defects will be corrected</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">5. Limitation of Liability</h2>
      <p className="text-slate-700 mb-3"><strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong></p>
      <p className="text-slate-700 mb-3">Free1099Calc, its owners, operators, employees, contractors, affiliates, and partners SHALL NOT BE LIABLE for any direct, indirect, incidental, special, consequential, or punitive damages arising from or related to:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Your use or inability to use the Site or Services</li>
        <li>Reliance on any information, calculations, or estimates provided by the Site</li>
        <li>Errors, inaccuracies, or omissions in any content or calculations</li>
        <li>Tax underpayments, overpayments, penalties, or interest resulting from use of our calculators</li>
        <li>IRS audits, disputes, or enforcement actions</li>
        <li>Lost profits, lost revenue, or business interruption</li>
        <li>Data loss or corruption</li>
        <li>Unauthorized access to your device or data</li>
        <li>Third-party conduct or content (including advertisers)</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">6. Acceptable Use Policy</h2>
      <p className="text-slate-700 mb-3">You agree NOT to:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Abuse or Overload:</strong> Make excessive requests, use bots, or attempt to overwhelm our servers</li>
        <li><strong>Scraping:</strong> Use automated tools to extract data or content from the Site</li>
        <li><strong>Reverse Engineering:</strong> Attempt to decompile, reverse engineer, or discover the source code of our calculators</li>
        <li><strong>Circumvent Security:</strong> Bypass or attempt to bypass any security measures or access restrictions</li>
        <li><strong>Interfere:</strong> Interfere with or disrupt the Site, servers, or networks</li>
        <li><strong>Illegal Activity:</strong> Use the Site for any unlawful purpose or in violation of any laws</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">7. Intellectual Property Rights</h2>
      <p className="text-slate-700 mb-3">All content on this Site, including but not limited to text, graphics, logos, calculator logic, code, design elements, and blog posts, is the property of Free1099Calc or its content suppliers and is protected by copyright, trademark, and other intellectual property laws.</p>
      <p className="text-slate-700 mb-3">You may:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Use the Site and calculators for personal, non-commercial purposes</li>
        <li>Print or download content for personal reference</li>
        <li>Share links to our articles on social media</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">8. Advertising</h2>
      <p className="text-slate-700 mb-4">
        We use <strong>Google AdSense</strong> and other advertising services to display ads on our Site. Advertisers may use cookies and tracking technologies. We do not control the content of advertisements and are not responsible for the accuracy or legality of advertised products or services. See our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> for more information.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">9. Privacy and Data Protection</h2>
      <p className="text-slate-700 mb-3">Your use of the Site is also governed by our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>, which explains how we collect, use, and protect your information. Key points:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Calculator Data:</strong> All calculations are performed locally in your browser. We do NOT send your financial data to our servers.</li>
        <li><strong>Cookies:</strong> We use cookies for analytics and advertising (Google AdSense).</li>
        <li><strong>No Sale of Data:</strong> We do not sell your personal information.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">10. Changes to Terms</h2>
      <p className="text-slate-700 mb-4">
        We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page with an updated "Last updated" date. Your continued use of the Site after changes constitutes acceptance of the updated Terms.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">11. Contact Information</h2>
      <p className="text-slate-700 mb-3">If you have questions about these Terms of Service, please contact us:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Email:</strong> legal@free1099calc.com</li>
        <li><strong>Website:</strong> <a href="https://free1099calc.com/contact" className="text-blue-600 underline">https://free1099calc.com/contact</a></li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">12. Acknowledgment</h2>
      <p className="text-slate-700 mb-3">By using Free1099Calc, you acknowledge that:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-6">
        <li>You have read and understood these Terms of Service</li>
        <li>You agree to be bound by these Terms</li>
        <li>You understand the Site provides educational information only and is NOT professional advice</li>
        <li>You will consult qualified professionals for tax, legal, and financial decisions</li>
        <li>You use the Site at your own risk</li>
      </ul>
    </article>
  );
}
